/**
 * Organization-scoped browser helpers.
 *
 * Mutations (org updates, Shopify credentials) live in tRPC, not here:
 * `set_org_shopify_credentials` is service_role-only, so credential writes
 * must go through the backend. This file is for client-side utilities only.
 */

import type { MasterInventoryReportRow } from '@threadheads/shared';
import { toCsvContent } from '@/lib/csv';

/**
 * Download master inventory report as CSV
 * Generates a comprehensive CSV with all SKUs across all locations
 */
export function downloadMasterInventoryCsv(
  data: MasterInventoryReportRow[],
  organizationSlug: string
): void {
  const headers = [
    'SKU',
    'Location',
    'Production Type',
    'Type',
    'Color',
    'Size',
    'SOH',
    'Available SOH',
    'Committed',
    'Unavailable',
    'Incoming',
    'Total Sold (Last 30 Days)',
    'Unique Orders (Last 30 Days)',
    'Sales Velocity (units/day)',
  ];

  const rows = data.map((row) => [
    row.sku,
    row.location_name,
    row.production_type || '',
    row.type || '',
    row.color || '',
    row.size || '',
    row.on_hand.toString(),
    row.available.toString(),
    row.committed.toString(),
    row.unavailable.toString(),
    row.incoming.toString(),
    row.total_sold_30d.toString(),
    row.unique_orders_30d.toString(),
    row.sales_velocity.toFixed(2),
  ]);

  const csvContent = toCsvContent([headers, ...rows]);

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  link.setAttribute('href', url);
  link.setAttribute('download', `master-inventory-${organizationSlug}-${date}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
